/*
defines main graph functionalities, interactions and interaction states
contains interactions for buttons, zooming and panning, status changes
*/

import LinkController from './link';
import NodeController from './node';
import api from 'utilities/api';
import {getNewDataFromAPI} from 'utilities/utilities';
import PathController from './path';
import MenuController from './menu';
import FileController from './file';
import GraphState from './state';
import KeyboardInputController from './input';
import BackupController from './backup';
import DaydreamsConnection from 'utilities/daydreams';

/**
 * this class represents the main controller component controlling the view and the model,
 * handling events, clicks, keys, etc.
 */
export default class GraphController {
    /**
     * makes a new graph controller given a graph model and a view
     * @param {Graph} model the graph model this controller should use
     * @param {GraphView} view the graph view this controller should use
     */
    constructor(model, view) {
        this.model = model;
        this.view = view;


        this.links = new LinkController(this, model, view);
        this.nodes = new NodeController(this, model, view);

        this.state = new GraphState(this);
        this.pathController = new PathController(this);
        this.menuController = new MenuController(this, model, view);
        this.fileController = new FileController(this, model, view);
        this.backupController = new BackupController(this, model, view);
        this.inputController = new KeyboardInputController(this, model, view);

        this.availableAPI = ['wiki', 'harvard', 'wiktionary', 'news', 'giphy'];

        this.daydreams = new DaydreamsConnection(this);

        if (this.model.nodes.length > 0 || this.model.links.length > 0) {
            this.importModel();
        }

        this.update();
    }

    /**
     * re-inits this controller when the view/model is updated
     */
    update() {
        this.initHandlers();
    }

    /**
     * pans/zooms the view when the svg is dragged
     */
    updateZoom() {
        this.view.container.attr('transform', d3.event.transform);
    }

    /**
     * initializes the instructions screen on connection
     */
    initInstructions(){
        while (this.state.is('start')){
            const newGraphView = this.view.initInstructions();
            this.update();
            return newGraphView;
        }
    }

    /**
     * initializes/defines all event (keyboard, mouse) handlers in this graph,
     * including handlers for nodes and links
     */
    initHandlers() {
        this.nodes.initHandlers();
        this.links.initHandlers();
        this.menuController.initHandlers();
        this.initPanHandler();
        this.fileController.initHandlers();
        this.inputController.initHandlers();
    }

    /**
     * initializes the handlers for panning the view
     */
    initPanHandler() { // no pun intended
        this.view.bg.call(d3.zoom()
                .on('zoom', () => this.updateZoom()))
            // .on('wheel.zoom', null)
            .on('dblclick.zoom', null); // double click zoom disabled
    }

    /**
     * disables the handler for panning and zooming
     */
    disablePanHandler() {
        this.view.svg.on('.zoom', null);
    }

    // TODO convert types to enums
    // TODO convert argments to destructured?
    /**
     * adds a node of the given type and API to the model and view
     * @param {string} type the type of node to add: image, wiki, connector
     * @param {Object} [apiSource] (optional) information about the node's api
     * @param {Object} [apiResponse] (optional) information about the node's api response
     * @param {number} [x] (optional) the starting x position
     * @param {number} [y] (optional) the starting y position
     * @return {Node} the new model node added
     */
    addNode(type, apiSource = {}, apiResponse = {}, x = this.view.width / 2, y = this.view.height / 2) {
        // check for existing nodes
        // debugger;
        const newNode = this.model.addNode(type, apiSource, apiResponse); // update model
        const newNodeView = this.view.addNode(newNode, x, y); // update view
        newNode.setUpdateCallback(newNodeView.updateCallback); // when model updates, update view
        this.update(); // update controller
        return newNodeView;
    }

    // checkExistingNode(title, type){
    //     let allNodes = this.model.nodes;
    //     for (let i in allNodes){
    //         if (allNodes[i].type === type && allNodes[i].apiSource.title(allNodes[i].apiResponse) === title) {
    //             console.log('existing node is ', allNodes[i])
    //             return true;
    //         }
    //     }
    //     return false;
    // }


    /**
     * given an image object with a URL and dimensions, makes a new image node
     *
     * @param {Object} image information about the image
     * @param {number} [x] (optional) horizontal position of the new node
     * @param {number} [y] (optional) vertical position of the new node
     * @return {LocalImageNode} the new model node added
     */
    addLocalImageNode(image, x = this.view.width / 2, y = this.view.height / 2) {
        const newNode = this.model.addLocalImageNode(image);
        const newNodeView = this.view.addNode(newNode, x, y);
        newNode.setUpdateCallback(newNodeView.updateCallback);
        this.update();
        return newNodeView;
    }

    /**
     * add a connector node with the given text
     * @param {string} text the thing this node is about
     * @param {number} [x] (optional) where to put this node
     * @param {number} [y] (optional) where to put this node
     * @return {ConnectorNode} the new model node added
     */
    addConnectorNode(text = 'connector', x = this.view.width / 2, y = this.view.height / 2) {
        const newNode = this.model.addConnectorNode(text);
        const newNodeView = this.view.addNode(newNode, x, y);
        this.update();
        return newNodeView;
    }

    /**
     * adds a link (in the view and model) between the given two node indices
     * @param {string} type the type of link to add
     * @param {number} source the id of the first node to connect
     * @param {number} target the id of the second node
     * @return {Link} the new link added
     */
    addLink(type = '', source, target) {
        let newLink = this.model.addLink(type, source, target); // update model
        let newLinkView = this.view.addLink(newLink); // update view
        this.update(); // update controller
        return newLinkView;
    }

    /**
     * clears the contents of the graph
     */
    clearGraph(){
        this.view.nodes = this.view.nodes.slice(0,1);
        this.view.links = [];
        this.view.restart();

        this.model.nodes = this.model.nodes.slice(0,1);
        this.model.links = [];
    }

    /**
     * remove the given node
     * @param {number} id the id of the node to remove
     */
    removeNode(id) {
        this.view.removeNode(id);
        this.model.removeNode(id);
    }

    /**
     * remove the given link from the model and view
     * @param {number} id the id of the link to remove
     */
    removeLink(id) {
        this.view.removeLink(id);
        this.model.removeLink(id);
    }

    /**
     * creates a new connector between the two nodes of the link
     * @param {number} id id of the link to be interleft
     * @param {string} query what connects these nodes
     */
    interleave(id, query) {
        const oldLink = this.view.getLink(id);
        const source = oldLink.source;
        const target = oldLink.target;
        const newNodeX = oldLink.x;
        const newNodeY = oldLink.y;

        this.removeLink(id);

        const newNode = this.addConnectorNode(query, newNodeX, newNodeY);

        const newLink1 = this.addLink('', source.id, newNode.id);
        this.view.getLink(newLink1.id).reinforceLink(5);

        const newLink2 = this.addLink('', newNode.id, target.id);
        this.view.getLink(newLink2.id).reinforceLink(5);

        // this.sproutNewNodes(query, newNodeX, newNodeY, newNode);

        return newNode;
    }

    /**
     * sprout new nodes based on the given node and a query
     * @param {number} id the node to be expanded
     * @param {string} query what to expand on
     */
    expand(id, query) {
        let oldNode = this.view.getNode(id);

        let newNode = this.addConnectorNode(query, oldNode.x, oldNode.y);
        let newLink = this.addLink('', oldNode.id, newNode.id);

        this.sproutNewNodes(query, newNode);
    }

    /**
     * makes new nodes coming out of the given node with all available APIs, given a search term, or random if left empty
     * @param {string} query what to search the APIs for
     * @param {Node} rootNode the root node to use
     */
    makeNodesIfAPIResponds(query, done, rootNode) {
        let newNodes = [];
        let allNodes = this.model.nodes;

        this.availableAPI.forEach((apiName) => {
            let apiResponse = {};
            let apiSource;
            if (!this.menuController.apisOff.has(apiName)) {
                if (query === '') {
                    if (!api[apiName].random) {
                        return;
                    }
                    apiSource = api[apiName].random();
                } else {
                    if (!api[apiName].search) {
                        return;
                    }
                    apiSource = api[apiName].search(query);
                }

                getNewDataFromAPI(apiSource, apiResponse, () => done(apiSource, apiResponse, rootNode, newNodes), () => {
                    this.view.div.classed('api-error', true);
                    window.clearTimeout(this.view.errorMessageTimeout);
                    this.view.errorMessageTimeout = window.setTimeout(function() {
                        this.view.div.classed('api-error', false);
                    }, 1500);
                });
            }
        });
    }

    /**
     * makes new related nodes next to the root node.
     * @param {string} query what to search
     * @param {Node} rootNode the node to start at
     */
    mosaic(query, rootNode) {
        console.log(`mosaic from ${rootNode.node.title}`);
        rootNode.fix();
        this.makeNodesIfAPIResponds(query, (apiSource, apiResponse, rootNode, newNodes) => this.makeSlaveChildNode(apiSource, apiResponse, rootNode, newNodes), rootNode);
    }

    /**
     * given an API and its response, makes a new node connected to the given root node
     * the new node is translucent and shrunken until it is selected
     *
     * [this is called only if API has returned something]
     * [helper function for mosaic]
     *
     * @param {Object} apiSource the source of the API of the new node
     * @param {Object} apiResponse the response of the API for the new node
     * @param {NodeView} rootNode the root node to connect with the new node
     * @param {Array<NodeView>} newNodes the list of child nodes having already been created so far
     */
    makeSlaveChildNode(apiSource, apiResponse, rootNode, newNodes) {
        // console.log('api response in controller', apiResponse);
        // console.log('nodes in model', this.model.nodes);
        if (!apiSource.isEmpty(apiResponse)) {
            let offsetX = rootNode.width;
            let offsetY = (rootNode.defaultHeight * .6) * (newNodes.length - 1);
            let newNode = this.addNode(apiSource.type, apiSource, apiResponse, rootNode.x, rootNode.y);
            // if (!newNode) return false;

            newNode.shrink();
            newNode.fade();
            newNode.fx = rootNode.x + offsetX;
            newNode.x = newNode.fx;
            newNode.fy = rootNode.y + offsetY;
            newNode.y = newNode.fy;
            let newLink = this.addLink('', rootNode.id, newNode.id);
            newLink.hide();
            newNodes.push(newNode);
            console.log(
                `
                root at ${rootNode.x}, ${rootNode.y}\n
                new ${newNode.node.title} ${newNode.type} at ${newNode.x}, ${newNode.y}\n
                offset by ${offsetX}, ${offsetY}
                `
            );
        }
        this.view.div.classed('mosaic', false);
    }

    /**
     * save function
     */
    save(){
        console.log('view nodes', this.view.nodes);
        console.log('view links', this.view.links);
        console.log('model nodes', this.model.nodes);
        console.log('model links', this.model.links);

        // this.model.backup();
        // this.view.backup();
    }

    castToDevice(device) {
      console.log("the device passed into castToDevice is:",device);
      this.state.set('CASTING');
      this.currentCastDevice = device;
    }

    showCastPopup() {
      console.log("showcastpopup is called");
      // shouldn't this be in a view somewhere?
      this.view.launchMenuContainer.classed('on', false);
      // alert("[Cast popup! 1. gives instructions 2. adds another device to sideways]\n\nDesign Daydreams Viewer:\nCast images from Looking Sideways" +
      //   "to your device & experience them through your low-tech AR viewer with the following QR code\n\n[QR code]");
      let anotherDeviceKey = 'cast'+(this.view.newmenu.length);
      let anotherDeviceName = 'Cast to Daydreams Viewer ' + this.view.newmenu.length;
      let anotherDevice = {
        key: anotherDeviceKey,
        name: anotherDeviceName
      };
      // let anotherDevice = {
      //   key: 'cast4',
      //   name: 'Device 4'
      // };
      this.view.newmenu.push(anotherDevice);
      console.log("the first key is: " + this.view.newmenu[0].key + " AND the new last item in newmenu is: " + this.view.newmenu[this.view.newmenu.length-1].name);
      this.view.initCast();
      //initialize graph again:
      this.addConnectorNode('.', -this.view.width/2, -this.view.height /2);
    }

    /**
     * makes new free nodes given a keyword and a source node
     * @param {*} query the keyword to search for
     * @param {*} rootNode the source node to start at
     */
    sproutNewNodes(query, rootNode) {
        this.makeNodesIfAPIResponds(query, (apiSource, apiResponse, rootNode) => this.makeFreeChildNode(apiSource, apiResponse, rootNode), rootNode);
    }

    /**
     * given an API and its response, makes a new node connected to the given root node
     * the new node is full-size and does not follow the root node
     *
     * [this is called only if API has returned something]
     * [helper function for sproutnewnodes]
     *
     * @param {Object} apiSource the source of the API of the new node
     * @param {Object} apiResponse the response of the API for the new node
     * @param {NodeView} rootNode the root node to connect with the new node
     */
    makeFreeChildNode(apiSource, apiResponse, rootNode) {
        if (!apiSource.isEmpty(apiResponse)) {
            let randomX = Math.floor(Math.random() * 100) - 50;
            let randomY = Math.floor(Math.random() * 100) - 50;
            let newNode2 = this.addNode(apiSource.type, apiSource, apiResponse, rootNode.x + randomX, rootNode.y + randomY);
            let newLink = this.addLink('', rootNode.id, newNode2.id);
        }
    }


}
